// Deterministic 2D facial geometry analysis (pipeline Stage 2 + diagnostic
// Stage 6/7). Turns facePrep's normalized landmarks into a supplementary
// "what's strong / what's addressable" read that sits ALONGSIDE the Gemini
// verdict — never replacing the headline score.
//
//   facePrep landmarks (output-frame fractions)
//     ─▶ px = {x*1080, y*1440}   (de-anisotropize)
//     ─▶ computeMetrics ─▶ scoreMetric per key
//     ─▶ { metrics: [...], overall, strong[], addressable[] } | null
//
// Runs only on a facePrep-normalized crop (landmarks present); returns null
// otherwise so the verdict still renders without it.
import { OUT_W, OUT_H } from '../facePrep/constants';
import { METRIC_KEYS, STRONG_THRESHOLD } from './constants';
import { computeMetrics } from './metrics';
import { scoreMetric } from './scoring';

// Minimum landmark count for the indices we read (highest index is 454).
const MIN_LANDMARKS = 468;

// fractions → pixel-space points (common unit so x/y ratios are isotropic).
function toPixels(landmarks) {
  return landmarks.map((lm) => ({ x: lm.x * OUT_W, y: lm.y * OUT_H }));
}

// landmarks: array of { x, y, z } output-frame fractions (or null).
// Returns the structured geometry read, or null when unavailable/malformed.
export function analyzeGeometry(landmarks) {
  if (!Array.isArray(landmarks) || landmarks.length < MIN_LANDMARKS) return null;
  let pts;
  try {
    pts = toPixels(landmarks);
  } catch (_e) {
    return null;
  }

  let raw;
  try {
    raw = computeMetrics(pts);
  } catch (_e) {
    return null;
  }

  const metrics = METRIC_KEYS.map((key) => scoreMetric(key, raw[key])).filter(
    Boolean,
  );
  if (!metrics.length) return null;

  const overall = Math.round(
    metrics.reduce((sum, m) => sum + m.score, 0) / metrics.length,
  );
  return {
    metrics,
    overall,
    strong: metrics.filter((m) => m.score >= STRONG_THRESHOLD).map((m) => m.key),
    addressable: metrics
      .filter((m) => m.score < STRONG_THRESHOLD)
      .map((m) => m.key),
  };
}
